import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Loader2, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import logger from '@/lib/logger';

const GRADE_STYLES = {
  A: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  B: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', dot: 'bg-sky-500' },
  C: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
  D: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
  F: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
};

function ComponentBar({ comp }) {
  const pct = comp.max > 0 ? Math.round((comp.score / comp.max) * 100) : 0;
  const fillColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div data-testid={`compliance-comp-${comp.key}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium text-slate-700">{comp.label}</span>
        <span className="text-[10px] text-slate-500 font-mono">
          {comp.score}/{comp.max}
        </span>
      </div>
      <div className="w-full h-1.5 bg-slate-100 rounded-sm overflow-hidden">
        <div className={`h-full ${fillColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[10px] text-slate-400 mt-1 leading-snug">{comp.detail}</p>
    </div>
  );
}

function Sparkline({ points }) {
  if (!points || points.length < 2) {
    return (
      <p className="text-[10px] text-slate-400 italic" data-testid="compliance-sparkline-empty">
        Trend will appear after a few daily snapshots.
      </p>
    );
  }
  const W = 220;
  const H = 36;
  const pad = 2;
  const scores = points.map((p) => p.score);
  const minS = Math.min(...scores, 0);
  const maxS = Math.max(...scores, 100);
  const range = Math.max(1, maxS - minS);
  const stepX = (W - pad * 2) / (points.length - 1);
  const coords = points.map((p, i) => {
    const x = pad + i * stepX;
    const y = H - pad - ((p.score - minS) / range) * (H - pad * 2);
    return [x, y];
  });
  const d = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${d} L${coords[coords.length - 1][0].toFixed(1)},${H - pad} L${pad},${H - pad} Z`;
  const last = points[points.length - 1];
  const first = points[0];
  const delta = last.score - first.score;
  const trendColor = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-slate-500';
  const trendSymbol = delta > 0 ? '\u2197' : delta < 0 ? '\u2198' : '\u2192';
  return (
    <div data-testid="compliance-sparkline">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">
          Last {points.length} day{points.length === 1 ? '' : 's'}
        </span>
        <span className={`text-[10px] font-semibold ${trendColor}`} data-testid="compliance-sparkline-delta">
          {trendSymbol} {delta > 0 ? '+' : ''}{delta}
        </span>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block">
        <path d={area} fill="rgba(0,71,171,0.08)" />
        <path d={d} fill="none" stroke="#0047AB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle
          cx={coords[coords.length - 1][0]}
          cy={coords[coords.length - 1][1]}
          r="2.5"
          fill="#0047AB"
        />
      </svg>
    </div>
  );
}

export function ComplianceHealthWidget() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getComplianceHealth().catch((e) => { logger.error(e); return null; }),
      api.getComplianceHealthHistory(30).catch(() => ({ data: { items: [] } })),
    ]).then(([scoreRes, histRes]) => {
      if (scoreRes) setData(scoreRes.data);
      setHistory(histRes?.data?.items || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        className="border border-slate-200 rounded-sm bg-white p-5 flex items-center justify-center"
        data-testid="compliance-health-loading"
      >
        <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
      </div>
    );
  }
  if (!data) return null;

  const g = GRADE_STYLES[data.grade] || GRADE_STYLES.F;

  return (
    <div
      className="border border-slate-200 rounded-sm bg-white overflow-hidden"
      data-testid="compliance-health-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#0047AB]" />
          <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide">
            SMS Compliance Health
          </h3>
        </div>
        <Link
          to="/admin/sms-consent-log"
          className="text-[11px] text-[#0047AB] hover:underline inline-flex items-center gap-1"
          data-testid="compliance-health-cta"
        >
          View Audit Log <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Score */}
      <div className="px-5 py-4 flex items-center gap-5 border-b border-slate-100">
        <div
          className={`flex-shrink-0 w-20 h-20 rounded-sm border ${g.border} ${g.bg} flex flex-col items-center justify-center`}
          data-testid="compliance-health-score-card"
        >
          <span className={`text-3xl font-bold ${g.text} leading-none`} data-testid="compliance-health-score">
            {data.score}
          </span>
          <span className="text-[9px] uppercase tracking-wide text-slate-500 mt-0.5">/ 100</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${g.dot}`} />
            <span className={`text-sm font-semibold ${g.text}`} data-testid="compliance-health-grade">
              Grade {data.grade}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 mb-2 leading-snug">
            A single audit-readiness pulse for your A2P 10DLC SMS program.
          </p>
          <Sparkline points={history} />
        </div>
      </div>

      {/* Component breakdown */}
      <div className="px-5 py-4 space-y-3">
        {data.components.map((c) => (
          <ComponentBar key={c.key} comp={c} />
        ))}
      </div>
    </div>
  );
}
